import { FC, SVGProps, useEffect, useRef, useState } from 'react'
import { ReactComponent as GlobeIcon } from 'src/assets/svg/Globe.svg'
import { Skeleton } from 'src/components/Skeleton'
import { Tooltip } from 'src/components/Tooltip'
import { CampaignsOverviewSourceWinner } from 'src/services/__generated__/api'
import { platformDisplayLabel } from 'src/constants/platformLabels'
import { SOCIAL_ICONS } from 'src/constants/socialIcons'
import styles from './negativeMentionsCard.module.scss'

// .platformRow is min-height 32 inside a 140px .box, and .mentions is 14px
// (negativeMentionsCard.module.scss). Mirrors one .box of the card.
const BoxSkeleton: FC = () => (
  <>
    <Skeleton height={32} width={140} />
    <Skeleton height={14} width={72} />
  </>
)

interface TopSourceCardProps {
  hasData?: boolean
  /** Hide the globe fallback for names that aren't a known social platform
   *  (e.g. operator names on the inbox overview). Platform icons still show. */
  hideUnknownSourceIcon?: boolean
  icon?: FC<SVGProps<SVGSVGElement>>
  isLoading?: boolean
  mostNegative?: CampaignsOverviewSourceWinner
  mostPositive?: CampaignsOverviewSourceWinner
  title?: string
  unitLabel?: string
}

export const TopSourceCard: FC<TopSourceCardProps> = ({
  hasData = false,
  hideUnknownSourceIcon = false,
  icon: Icon = GlobeIcon,
  isLoading = false,
  mostNegative,
  mostPositive,
  title = 'Top Source',
  unitLabel = 'Mentions',
}) => {
  const positiveTextRef = useRef<HTMLDivElement>(null)
  const negativeTextRef = useRef<HTMLDivElement>(null)
  const [isPositiveTruncated, setIsPositiveTruncated] = useState(false)
  const [isNegativeTruncated, setIsNegativeTruncated] = useState(false)

  // Title-cases the source name, except where the platform has a label of
  // its own (X keeps its brand casing rather than becoming "X (twitter)").
  const capitalize = (str: string) =>
    platformDisplayLabel(
      str,
      str.replace(/\b\w/g, (c) => c.toUpperCase())
    )

  const getSourceIcon = (sourceName: string) => {
    const name = sourceName.toLowerCase().trim()
    if (SOCIAL_ICONS[name]) {
      // booking/expedia brand svgs render black & white in this card
      return ['booking', 'expedia'].includes(name) ? (
        <span className={styles.monoBrandIcon}>{SOCIAL_ICONS[name]}</span>
      ) : (
        SOCIAL_ICONS[name]
      )
    }
    return hideUnknownSourceIcon ? null : <GlobeIcon />
  }

  const positiveData =
    hasData && mostPositive && (mostPositive.positiveMentions || 0) > 0
      ? {
          icon: mostPositive.source || '',
          mentions: mostPositive.positiveMentions || 0,
          text: mostPositive.source || 'Unknown Source',
        }
      : null
  const negativeData =
    hasData && mostNegative && (mostNegative.negativeMentions || 0) > 0
      ? {
          icon: mostNegative.source || '',
          mentions: mostNegative.negativeMentions || 0,
          text: mostNegative.source || 'Unknown Source',
        }
      : null

  const positiveIcon = positiveData ? getSourceIcon(positiveData.icon) : null
  const negativeIcon = negativeData ? getSourceIcon(negativeData.icon) : null

  useEffect(() => {
    const checkTruncation = (element: HTMLDivElement | null) => {
      if (!element) return false
      return element.scrollHeight > element.clientHeight
    }

    setIsPositiveTruncated(checkTruncation(positiveTextRef.current))
    setIsNegativeTruncated(checkTruncation(negativeTextRef.current))
  }, [positiveData?.text, negativeData?.text])

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <Icon className={styles.icon} />
        <h3 className={styles.title}>{title}</h3>
      </div>
      <div className={styles.container}>
        <div className={styles.box}>
          <div className={styles.label}>
            <span className={styles.dot}></span>
            <span className={styles.labelText}>Most Positive</span>
          </div>
          {/* Loading first — "No Data" here was a claim about data that had
              not arrived yet. */}
          {isLoading ? (
            <BoxSkeleton />
          ) : positiveData ? (
            <>
              {isPositiveTruncated ? (
                <Tooltip
                  overlay={capitalize(positiveData.text)}
                  overlayClassName="topCampaignTooltip"
                  placement="top"
                >
                  <div className={styles.platformRow} ref={positiveTextRef}>
                    {positiveIcon && (
                      <span className={styles.platformIcon}>
                        {positiveIcon}
                      </span>
                    )}
                    <span className={styles.platformName}>
                      {capitalize(positiveData.text)}
                    </span>
                  </div>
                </Tooltip>
              ) : (
                <div className={styles.platformRow} ref={positiveTextRef}>
                  {positiveIcon && (
                    <span className={styles.platformIcon}>{positiveIcon}</span>
                  )}
                  <span className={styles.platformName}>
                    {capitalize(positiveData.text)}
                  </span>
                </div>
              )}
              <div className={styles.mentions}>
                {positiveData.mentions.toLocaleString()}{' '}
                {positiveData.mentions === 1
                  ? unitLabel.replace(/s$/, '')
                  : unitLabel}
              </div>
            </>
          ) : (
            <div className={styles.noData}>No Data</div>
          )}
        </div>
        <div className={styles.divider}></div>
        <div className={styles.box}>
          <div className={styles.label}>
            <span className={styles.dotNegative}></span>
            <span className={styles.labelText}>Most Negative</span>
          </div>
          {isLoading ? (
            <BoxSkeleton />
          ) : negativeData ? (
            <>
              {isNegativeTruncated ? (
                <Tooltip
                  overlay={capitalize(negativeData.text)}
                  overlayClassName="topCampaignTooltip"
                  placement="top"
                >
                  <div className={styles.platformRow} ref={negativeTextRef}>
                    {negativeIcon && (
                      <span className={styles.platformIcon}>
                        {negativeIcon}
                      </span>
                    )}
                    <span className={styles.platformName}>
                      {capitalize(negativeData.text)}
                    </span>
                  </div>
                </Tooltip>
              ) : (
                <div className={styles.platformRow} ref={negativeTextRef}>
                  {negativeIcon && (
                    <span className={styles.platformIcon}>{negativeIcon}</span>
                  )}
                  <span className={styles.platformName}>
                    {capitalize(negativeData.text)}
                  </span>
                </div>
              )}
              <div className={styles.mentions}>
                {negativeData.mentions.toLocaleString()}{' '}
                {negativeData.mentions === 1
                  ? unitLabel.replace(/s$/, '')
                  : unitLabel}
              </div>
            </>
          ) : (
            <div className={styles.noData}>No Data</div>
          )}
        </div>
      </div>
    </div>
  )
}
